/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import ReviewAnalytics from './reviewAnalytics';
import {
    AccessKey, ActivateHandle, ReviewChartData, ReviewSalesPeriod, ReviewStatsDisplay, ReviewTab
} from '../../util/interfaces';
import {onSetCrumbs} from "../../util/events";
import {ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {AppContext} from "../misc/appContext";
import {sendGaPageView} from "../../util/analytics";
import CustomPeriodController from "../salesDashboard/customPeriod/customPeriodController";
import useRight from "../misc/useRight";
import {getReviewChartData, getReviewTotals} from "../../util/requestSender";
import {ISO_DATE_FORMAT} from "../../util/constants";
import {getReviewAnalyticsMemValue, setReviewAnalyticsMemValue} from "../../util/reviewAnalyticsUtil";

export function getReviewDatesFromPeriod(period: ReviewSalesPeriod): [IDate | null, IDate | null] {
    if (period === ReviewSalesPeriod.TOTAL) return [null, null];
    const toDate = IDate.now();
    let fromDate: IDate = toDate.clone();
    if (period === ReviewSalesPeriod.SEVEN_DAYS) fromDate.subtract(7, 'days');
    else if (period === ReviewSalesPeriod.THIRTY_DAYS) fromDate.subtract(30, 'days');
    return [fromDate, toDate];
}

type LoadParams = {fromDate: IDate | null; toDate: IDate | null; period?: ReviewSalesPeriod};

export default function ReviewAnalyticsController() {
    const [accessProducts, accessFeed, accessExport] = useRight(AccessKey.PAGE_REVIEWS_PRODUCTS,
        AccessKey.PAGE_REVIEWS_FEED, AccessKey.PAGE_REVIEWS_EXPORT);
    const context = useContext(AppContext);

    const allowedTabs = useMemo(() => {
        const allowed: Array<ReviewTab> = [];
        if (accessFeed) allowed.push(ReviewTab.FEED);
        if (accessProducts) allowed.push(ReviewTab.PRODUCTS);
        if (accessExport) allowed.push(ReviewTab.EXPORT);
        return allowed;
    }, [accessProducts, accessFeed, accessExport]);
    const defaultTab = useMemo(() => {
        return allowedTabs[0];
    }, [allowedTabs]);

    const defaultPeriod = getReviewAnalyticsMemValue('period') ?? ReviewSalesPeriod.THIRTY_DAYS;
    const [defaultFrom, defaultTo] = getReviewDatesFromPeriod(defaultPeriod);
    const [periodValue, setPeriodValue] = useState<ReviewSalesPeriod>(defaultPeriod);
    const [fromDate, setFromDate] = useState<IDate | null>(defaultFrom);
    const [toDate, setToDate] = useState<IDate | null>(defaultTo);
    const [tab, setTab] = useState<ReviewTab>(getTabFromParams());
    const [loadingChart, setLoadingChart] = useState(true);
    const [loadingCountStats, setLoadingCountStats] = useState(true);
    const [chartData, setChartData] = useState<Record<string, ReviewChartData>>({});
    const [totalPositive, setTotalPositive] = useState(0);
    const [totalNegative, setTotalNegative] = useState(0);
    const [totalPositiveIncrease, setTotalPositiveIncrease] = useState(0);
    const [totalNegativeIncrease, setTotalNegativeIncrease] = useState(0);
    const [npsScore, setNpsScore] = useState(0);
    const [periodNpsScore, setPeriodNpsScore] = useState(0);
    const [statsDisplay, setStatsDisplay] = useState<ReviewStatsDisplay>(ReviewStatsDisplay.STATS_ONLY);

    const customPeriodRef = useRef<ActivateHandle>(null);

    function getTabFromParams(): ReviewTab {
        let _tab: ReviewTab = getReviewAnalyticsMemValue('tab') ?? defaultTab;
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has('tab')) {
            const tab = searchParams.get('tab')!;
            if (tab === 'feed' && allowedTabs.includes(ReviewTab.FEED)) _tab = ReviewTab.FEED;
            else if (tab === 'settings' && allowedTabs.includes(ReviewTab.SETTINGS)) _tab = ReviewTab.SETTINGS;
        }
        setReviewAnalyticsMemValue('tab', _tab);
        return _tab;
    }

    function onClickStat(stat: 'all' | 'positive' | 'negative') {
        return function() {
            if (tab !== ReviewTab.FEED) onChangeTab(ReviewTab.FEED)();
            // Wait for feed tab to display
            window.setTimeout(() => {
                document.dispatchEvent(new CustomEvent('reviewStatClick', {detail: stat}));
            }, 1);
        }
    }

    function onChangeStatsDisplay() {
        if (statsDisplay === ReviewStatsDisplay.FULL) setStatsDisplay(ReviewStatsDisplay.STATS_ONLY);
        else setStatsDisplay(ReviewStatsDisplay.FULL);
    }

    function onChangePeriod(selectedPeriod: ReviewSalesPeriod | '') {
        if (selectedPeriod === ReviewSalesPeriod.CUSTOM) return customPeriodRef.current?.activate();
        if (selectedPeriod === periodValue || selectedPeriod === '') return;
        const [fromDate, toDate] = getReviewDatesFromPeriod(selectedPeriod);
        setPeriodValue(selectedPeriod);
        setFromDate(fromDate);
        setToDate(toDate);
        setReviewAnalyticsMemValue('period', selectedPeriod);
        setReviewAnalyticsMemValue('periodFrom', fromDate);
        setReviewAnalyticsMemValue('periodTo', toDate);
        loadChartData({fromDate, toDate, period: selectedPeriod});
        loadReviewTotals({fromDate, toDate});
    }

    function onSubmitCustomPeriod(fromDate: IDate, toDate: IDate) {
        const period = ReviewSalesPeriod.CUSTOM;
        setPeriodValue(period);
        setFromDate(fromDate);
        setToDate(toDate);
        setReviewAnalyticsMemValue('period', period);
        setReviewAnalyticsMemValue('periodFrom', fromDate);
        setReviewAnalyticsMemValue('periodTo', toDate);
        loadChartData({fromDate, toDate, period});
        loadReviewTotals({fromDate, toDate});
    }

    function onChangeTab(_tab: ReviewTab) {
        return function() {
            setReviewAnalyticsMemValue('tab', _tab);
            setTab(_tab);
        }
    }

    function loadReviewTotals(params: LoadParams) {
        const {account} = context.accountContext;
        if (!account) return;
        setLoadingCountStats(true);
        const _fromDate = params.fromDate?.format(ISO_DATE_FORMAT) ?? null;
        const _toDate = params.toDate?.format(ISO_DATE_FORMAT) ?? null;
        getReviewTotals({
            data: {account_id: account.id, from_date: _fromDate, to_date: _toDate},
            callback: (data: ResponseData) => {
                setLoadingCountStats(false);
                if (data.success) {
                    const {
                        total_negative, total_positive, total_negative_increase, total_positive_increase, nps_score,
                        period_nps_score
                    } = data;
                    setTotalNegative(total_negative)
                    setTotalPositive(total_positive);
                    setTotalNegativeIncrease(total_negative_increase);
                    setTotalPositiveIncrease(total_positive_increase);
                    setNpsScore(nps_score);
                    setPeriodNpsScore(period_nps_score);
                }
            }
        })
    }

    function loadChartData(params: LoadParams) {
        const {account} = context.accountContext;
        if (!account) return;
        setLoadingChart(true);
        getReviewChartData({
            data: {account_id: account.id, from_date: null, to_date: null},
            callback: (data: ResponseData) => {
                setLoadingChart(false);
                if (data.success) {
                    if (params?.period)
                        setPeriodValue(params.period);
                    setFromDate(params.fromDate)
                    setToDate(params.toDate);
                    setChartData(data.chart_data);
                }
            }
        })
    }


    useEffect(() => {
        loadChartData({fromDate: defaultFrom, toDate: defaultTo});
        loadReviewTotals({fromDate: defaultFrom, toDate: defaultTo});
        if (!allowedTabs.includes(tab)) setTab(allowedTabs[0]);
    }, [context.accountContext.account]);

    useEffect(() => {
        sendGaPageView('/review-analytics');
        onSetCrumbs([{label: 'Review Inzicht', route: '/review-analytics'}]);
    }, []);

    return (
        <>
            <ReviewAnalytics onChangePeriod={onChangePeriod} onChangeTab={onChangeTab} onClickStat={onClickStat}
                             onChangeStatsDisplay={onChangeStatsDisplay} periodValue={periodValue}
                             tab={tab} toDate={toDate} fromDate={fromDate} allowedTabs={allowedTabs}
                             chartData={chartData} loadingChart={loadingChart} loadingStats={loadingCountStats}
                             totalNegative={totalNegative} totalNegativeIncrease={totalNegativeIncrease}
                             totalPositive={totalPositive} totalPositiveIncrease={totalPositiveIncrease}
                             statsDisplay={statsDisplay} npsScore={npsScore} periodNpsScore={periodNpsScore}/>
            <CustomPeriodController ref={customPeriodRef} onSubmit={onSubmitCustomPeriod}/>
        </>
    )
}